import React, { useEffect, useRef, useState } from 'react';
import {
  addPasskey, conditionalMediationAvailable, dismissPasskeyOffer, isCancel, isPasskeyOnlyRefusal,
  listPasskeys, passkeyOfferDismissed, passkeysSupported, signInWithPasskey,
} from './passkeys';

// Minimal email/password modal. The cookie set by the BFF is HttpOnly
// so we don't see it in JS — `credentials: 'include'` lets the browser
// store and resend it. `onSuccess(profile)` is called with the JSON
// body's `profile` field so the parent can update state.
const LoginModal = ({ onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null); // 'login' | 'signup' | null
  // Sign-up is by invitation unless the server says otherwise; until it answers,
  // show the log-in form only.
  const [signupOpen, setSignupOpen] = useState(false);
  // passkey-login: shown only when the server switch is on AND this browser can do it.
  const [passkeysOn, setPasskeysOn] = useState(false);
  // After a password sign-in: {profile, accountId} while we offer to add a passkey.
  const [offer, setOffer] = useState(null);
  // passkey-only-accounts: signed in with an admin restore's one-time password → choose your own.
  const [mustChange, setMustChange] = useState(null);   // the sign-in response, held until done
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const autofill = useRef(null);   // AbortController of the email field's passkey autofill
  useEffect(() => {
    let live = true;
    fetch('/api/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => {
        if (!live || !me) return;
        setSignupOpen(me.signup_open === true);
        setPasskeysOn(me.passkeys === true && passkeysSupported());
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // Offer saved passkeys in the email field's autofill. Errors stay silent: the
  // form still works, and the button below is the explicit way in.
  useEffect(() => {
    if (!passkeysOn) return undefined;
    let live = true;
    const ctrl = new AbortController();
    autofill.current = ctrl;
    conditionalMediationAvailable().then((ok) => {
      if (!ok || !live) return;
      signInWithPasskey({ signal: ctrl.signal, conditional: true })
        .then((data) => { if (live && data) onSuccess(data.profile); })
        .catch(() => {});
    });
    return () => { live = false; ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeysOn]);

  const stopAutofill = () => { if (autofill.current) autofill.current.abort(); };

  const passkeySignIn = async () => {
    setError(null);
    stopAutofill();   // one WebAuthn request at a time
    setSubmitting('passkey');
    try {
      const data = await signInWithPasskey();
      if (data) onSuccess(data.profile);
    } catch (e) {
      if (!isCancel(e)) setError(e.message || String(e));
    } finally {
      setSubmitting(null);
    }
  };

  // After a password sign-in: offer a passkey once, unless they already have one.
  const finishPasswordSignIn = async (data) => {
    if (passkeysOn && !passkeyOfferDismissed(data.account_id)) {
      try {
        if ((await listPasskeys()).length === 0) {
          stopAutofill();
          setOffer({ profile: data.profile, accountId: data.account_id });
          return;
        }
      } catch { /* can't tell — don't offer */ }
    }
    onSuccess(data.profile);
  };

  const chooseNewPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPw.length < 8) { setError('Use at least 8 characters.'); return; }
    if (newPw !== newPw2) { setError('The passwords don’t match.'); return; }
    setSubmitting('change');
    try {
      const r = await fetch('/auth/password', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: password, new_password: newPw }),
      });
      if (r.status !== 204) {
        setError((await r.text()) || `${r.status} ${r.statusText}`);
        return;
      }
      const data = mustChange;
      setMustChange(null);
      await finishPasswordSignIn(data);
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(null);
    }
  };

  const closeOffer = () => {
    dismissPasskeyOffer(offer.accountId);
    onSuccess(offer.profile);
  };

  const acceptOffer = async () => {
    setError(null);
    setSubmitting('passkey');
    try {
      await addPasskey();
      closeOffer();
    } catch (e) {
      if (!isCancel(e)) setError(e.message || String(e));
    } finally {
      setSubmitting(null);
    }
  };

  const submit = async (mode) => {
    setError(null);
    setSubmitting(mode);
    try {
      const path = mode === 'signup' ? '/auth/signup' : '/auth/login';
      const body = mode === 'signup'
        ? {
            email, password,
            display_name: displayName || null,
            full_name: fullName || null,
            phone: phone || null,
          }
        : { email, password };
      const r = await fetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const text = await r.text();
        setError(isPasskeyOnlyRefusal(text)
          ? 'This account signs in with a passkey. Use “Sign in with a passkey”.'
          : (text || `${r.status} ${r.statusText}`));
        return;
      }
      const data = await r.json();
      if (mode === 'login' && data.must_change_password) {
        setMustChange(data);
        return;
      }
      await finishPasswordSignIn(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(null);
    }
  };

  const onFormSubmit = (e) => { e.preventDefault(); submit('login'); };

  const btnBase = {
    padding: '8px', borderRadius: '4px', cursor: 'pointer',
    fontSize: '0.9rem', border: '1px solid #ccc',
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
               display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', padding: '24px', borderRadius: '8px',
                 width: '320px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
                      justifyContent: 'center', marginBottom: '20px' }}>
          <img src="/aveqlogo.svg" alt="" style={{ height: '72px' }} />
          <span style={{ fontSize: '1.4rem', textTransform: 'uppercase',
                         letterSpacing: '0.05em' }}>AVE Q</span>
        </div>
        {mustChange ? (
          <form onSubmit={chooseNewPassword} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>Choose a new password</div>
            <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.4 }}>
              You signed in with a one-time password. Pick your own to finish signing in.
            </div>
            <input type="password" placeholder="New password (min 8 characters)" value={newPw}
              autoComplete="new-password" autoFocus aria-label="New password"
              onChange={(e) => setNewPw(e.target.value)}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
            <input type="password" placeholder="Confirm new password" value={newPw2}
              autoComplete="new-password" aria-label="Confirm new password"
              onChange={(e) => setNewPw2(e.target.value)}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
            {error && <div style={{ color: '#c00', fontSize: '0.8rem' }}>{error}</div>}
            <button type="submit" disabled={submitting !== null}
              style={{ ...btnBase, background: '#333', color: '#fff', border: 'none' }}>
              {submitting === 'change' ? '…' : 'Save and continue'}
            </button>
          </form>
        ) : offer ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>Add a passkey?</div>
            <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.4 }}>
              Next time, sign in with your fingerprint, face or screen lock instead of your password.
            </div>
            {error && <div style={{ color: '#c00', fontSize: '0.8rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <button type="button" onClick={acceptOffer} disabled={submitting !== null}
                style={{ ...btnBase, flex: 1, background: '#333', color: '#fff', border: 'none' }}>
                {submitting === 'passkey' ? '…' : 'Add a passkey'}
              </button>
              <button type="button" onClick={closeOffer} disabled={submitting !== null}
                style={{ ...btnBase, padding: '8px 16px', background: '#f0f0f0' }}>
                Not now
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={onFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input type="email" placeholder="Email" value={email}
            autoComplete={passkeysOn ? 'username webauthn' : 'username'}
            onChange={(e) => setEmail(e.target.value)} required autoFocus
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
          <input type="password" placeholder="Password" value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} required
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
          {signupOpen && <>
          <input type="text" placeholder="Display name (sign up only, optional)" value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
          <input type="text" placeholder="Full name (sign up only, optional)" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
          <input type="tel" placeholder="Phone (sign up only, optional)" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }} />
          </>}
          {error && <div style={{ color: '#c00', fontSize: '0.8rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button type="submit" disabled={submitting !== null}
              style={{ ...btnBase, flex: 1, background: '#333', color: '#fff', border: 'none' }}>
              {submitting === 'login' ? '…' : 'Log in'}
            </button>
            {signupOpen && (
              <button type="button" onClick={() => submit('signup')} disabled={submitting !== null}
                style={{ ...btnBase, flex: 1, background: '#333', color: '#fff', border: 'none' }}>
                {submitting === 'signup' ? '…' : 'Sign up'}
              </button>
            )}
            <button type="button" onClick={onClose}
              style={{ ...btnBase, padding: '8px 16px', background: '#f0f0f0' }}>
              Cancel
            </button>
          </div>
          {passkeysOn && (
            <button type="button" onClick={passkeySignIn} disabled={submitting !== null}
              style={{ ...btnBase, background: '#fff' }}>
              {submitting === 'passkey' ? '…' : 'Sign in with a passkey'}
            </button>
          )}
        </form>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
